import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { EsnIconName } from '../../../shared/icons';
import type { EsnButtonSize, EsnButtonVariant } from '../../../shared/types/atoms';
import { IconComponent } from '../icon/icon.component';

/**
 * The console's action button: one control height per size, six visual variants,
 * optional leading/trailing glyph.
 */
@Component({
  selector: 'esn-button',
  imports: [IconComponent],
  templateUrl: './button.component.html',
  styleUrl: './button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.esn-button-host--full]': 'fullWidth()' },
})
export class ButtonComponent {
  readonly variant = input<EsnButtonVariant>('primary');
  readonly size = input<EsnButtonSize>('md');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false);
  readonly fullWidth = input(false);
  readonly leadingIcon = input<EsnIconName | null>(null);
  readonly trailingIcon = input<EsnIconName | null>(null);

  /**
   * Set when the button opens a popup (e.g. an "Actions" menu), announced with its `expanded` state.
   */
  readonly haspopup = input<'menu' | 'dialog' | 'listbox' | null>(null);
  readonly expanded = input<boolean | null>(null);

  readonly clicked = output<MouseEvent>();

  protected readonly classes = computed(() => `esn-button esn-button--${this.variant()} esn-button--${this.size()}`);

  protected handleClick(event: MouseEvent): void {
    if (!this.disabled()) this.clicked.emit(event);
  }
}
